import { IGeoJson2DGeographicCoordinates } from '~/client/graph'
import Workflow from '~/client/src/shared/models/Workflow'
import { hasPreciseCoordinates } from '~/client/src/shared/utils/locationSnapshot'
import {
  IAncestorCoordinates,
  ILocationNode,
  LocationDisplayResolvedBy,
  walkHierarchy,
} from '~/client/src/shared/utils/resolveLocationDisplayContext'

import {
  IWorkflowPinCoordOverride,
  ownWorkflowPinCoord,
} from './workflowPinPoints'

/**
 * Where a workflow pin goes when the workflow has no precise location of its own.
 *
 * A workflow is tagged to a location, not to a coordinate, and that location may be one the map
 * does not draw — a Level inside a Building the globe shows only as a Building, say. So the pin
 * rides the nearest ancestor the map *does* draw, and failing that the project catch-all. Getting
 * this wrong either hides a workflow or plants it in the wrong place, which is why it is here with
 * tests rather than inline in the map view.
 */
export interface IPinAnchorContext {
  /** The location hierarchy, by id. */
  getLocation: (id: string) => ILocationNode | null
  /** Ids the map is currently drawing — globe items, or sitemap attributes off a whiteboard. */
  displayedItemIds: Set<string>
  /**
   * Whether this globe view is tagged to the project as a whole. Resolved by the caller, because
   * `LocationIntegration` overrides `is()` to match its integration type too, and a type list here
   * would quietly lose that.
   */
  isProjectTagged: boolean
  /** The first tagged location's id, which is the LBS scope when it is not the whole project. */
  lbsTagLocationId: string | null
  resolveCoordinates: (id: string) => IAncestorCoordinates | null
  /** Whether that location draws a marker circle a pin can visibly ride. */
  isAnchorCircleShown: (id: string) => boolean
}

// stopBeforeProject false: the project node is a legitimate anchor here — it is the catch-all.
const findAncestorInChain = <T>(
  ctx: IPinAnchorContext,
  locationId: string,
  visit: (node: ILocationNode) => T | null,
): T | null =>
  locationId
    ? walkHierarchy<T>(locationId, ctx.getLocation, visit, {
        stopBeforeProject: false,
      })
    : null

export const findNearestDisplayedInChain = (
  ctx: IPinAnchorContext,
  locationId: string,
): string | null =>
  findAncestorInChain<string>(ctx, locationId, node =>
    ctx.displayedItemIds.has(node.id) ? node.id : null,
  )

/** Otherwise a workflow only belongs here if its location sits under the globe's LBS tag. */
export const isUnderMapLbsTag = (
  ctx: IPinAnchorContext,
  locationId: string,
): boolean => {
  if (ctx.isProjectTagged) {
    return true
  }
  // An untagged globe has no scope to exclude by, so it excludes nothing: whether the workflow
  // shows is then decided by whether the map draws an ancestor for it to ride. Returning false here
  // meant a globe with no LBS tag drew no anchored workflow at all, however cleanly its anchor
  // resolved — shipped with STX1-382 and unreported until now.
  if (!ctx.lbsTagLocationId) {
    return true
  }
  return !!findAncestorInChain<string>(ctx, locationId, node =>
    node.id === ctx.lbsTagLocationId ? node.id : null,
  )
}

/**
 * Whether this map should draw the workflow at all.
 *
 * A workflow pinned to a precise location belongs to the globe it was pinned on and nowhere else.
 * Anything else has to be in scope for this globe and have somewhere to land.
 */
export const workflowPinnedGlobeIds = (workflow: Workflow): string[] =>
  (workflow?.locations ?? [])
    .map(l =>
      hasPreciseCoordinates(l.preciseLocation)
        ? l.preciseLocation.globeViewId
        : null,
    )
    .filter(Boolean)

export const isWorkflowOnMap = (
  ctx: IPinAnchorContext,
  permit: Workflow,
  selectedGlobeViewId: string,
): boolean => {
  const pinnedGlobeViewIds = workflowPinnedGlobeIds(permit)
  if (pinnedGlobeViewIds.length && selectedGlobeViewId) {
    return pinnedGlobeViewIds.includes(selectedGlobeViewId)
  }

  return (permit.locationsWithEquipment ?? []).some(l => {
    if (!isUnderMapLbsTag(ctx, l.id)) {
      return false
    }
    if (findNearestDisplayedInChain(ctx, l.id)) {
      return true
    }
    return ctx.isProjectTagged && !!ctx.resolveCoordinates(l.id)
  })
}

export const filterWorkflowsOnMap = (
  ctx: IPinAnchorContext,
  workflows: Workflow[],
  selectedGlobeViewId: string,
): Workflow[] =>
  workflows.filter(permit => isWorkflowOnMap(ctx, permit, selectedGlobeViewId))

const resolveWorkflowPinAnchor = (
  ctx: IPinAnchorContext,
  workflow: Workflow,
): IWorkflowPinCoordOverride | null => {
  // The predicate, not ownWorkflowPinCoord: no coordinate object to discard per workflow.
  if (hasPreciseCoordinates(workflow?.locations?.[0]?.preciseLocation)) {
    return null
  }

  // Uncached getter: concats two field scans per read.
  const locations = workflow.locationsWithEquipment ?? []

  for (const l of locations) {
    const anchorItemId = findNearestDisplayedInChain(ctx, l.id)
    const anchorCoord = anchorItemId && ctx.resolveCoordinates(anchorItemId)
    if (anchorCoord) {
      // Ride only the node the coordinate came from, and only if it draws a circle.
      const { coordinates, resolvedLocationId } = anchorCoord
      return {
        coord: coordinates,
        anchorItemId: ctx.isAnchorCircleShown(resolvedLocationId)
          ? resolvedLocationId
          : undefined,
      }
    }
  }

  if (!ctx.isProjectTagged) {
    return null
  }

  const catchall = locations.reduce<IAncestorCoordinates | null>(
    (acc, l) => acc ?? ctx.resolveCoordinates(l.id),
    null,
  )

  return catchall ? { coord: catchall.coordinates } : null
}

/**
 * The coordinate each pin is drawn at, for the workflows that have no precise location. GL
 * decluttering alone decides which of the resulting pins is visible.
 */
export const buildWorkflowPinCoordOverrides = (
  ctx: IPinAnchorContext,
  workflows: Workflow[],
): Map<string, IWorkflowPinCoordOverride> => {
  const overrides = new Map<string, IWorkflowPinCoordOverride>()

  for (const workflow of workflows) {
    const resolved = resolveWorkflowPinAnchor(ctx, workflow)
    if (resolved) {
      overrides.set(workflow.id, resolved)
    }
  }

  return overrides
}

/**
 * Where this map draws the workflow's pin, or null when it draws none. Membership and coordinate
 * together, so a caller cannot frame a globe the workflow does not belong to. Membership is
 * decided across all of its locations; the coordinate comes from the first, matching the renderer.
 */
export const resolveWorkflowPinCoordinate = (
  ctx: IPinAnchorContext,
  workflow: Workflow,
  selectedGlobeViewId: string,
): IGeoJson2DGeographicCoordinates | null => {
  if (!workflow || !isWorkflowOnMap(ctx, workflow, selectedGlobeViewId)) {
    return null
  }

  return (
    resolveWorkflowPinAnchor(ctx, workflow)?.coord ??
    ownWorkflowPinCoord(workflow)
  )
}

/**
 * The candidate map view that would draw this workflow's pin, or null when none would.
 *
 * `wouldDrawPin` is the renderer's own predicate evaluated against a candidate map, so nomination
 * and arrival cannot disagree. An earlier version predicted the answer from the display-context
 * tier instead, which was wrong in both directions: `projectTag` was rejected though its catch-all
 * does draw, and the tag tiers were accepted though they prove LBS scope only — the map may draw
 * nothing for the workflow. That is STX1-1168.
 *
 * The display context still RANKS candidates by tier; it just no longer decides. Because it names
 * one globe per location, a rejected proposal drops out and the rest are re-proposed, so a
 * lower-ranked map that genuinely draws stays reachable.
 */
export const resolveWorkflowSourceGlobe = (
  workflow: Workflow,
  candidateGlobeIds: string[],
  resolveDisplayContext: (
    locationId: string,
    candidateGlobeIds: string[],
  ) => {
    globeViewId: string | null
    resolvedBy: LocationDisplayResolvedBy | null
  } | null,
  wouldDrawPin: (globeViewId: string) => boolean,
): string | null => {
  if (!workflow || !candidateGlobeIds?.length) {
    return null
  }

  // Only with a globe id: a stored coordinate without one is pinned nowhere. No gate here — this
  // coordinate IS what the renderer draws, so asking could only ever agree.
  const placed = workflow.locations?.[0]?.preciseLocation
  if (hasPreciseCoordinates(placed) && placed.globeViewId) {
    return candidateGlobeIds.includes(placed.globeViewId)
      ? placed.globeViewId
      : null
  }

  // A pin anywhere decides membership everywhere, so the tiers cannot nominate past it — but the
  // pinned maps still can: the pin may sit on a later location or on Equipment.
  const pinnedGlobeIds = workflowPinnedGlobeIds(workflow)
  if (pinnedGlobeIds.length) {
    return (
      pinnedGlobeIds.find(
        id => candidateGlobeIds.includes(id) && wouldDrawPin(id),
      ) ?? null
    )
  }

  // A refused proposal leaves the pool so the next-ranked map gets its turn.
  let remaining = candidateGlobeIds
  while (remaining.length) {
    const proposed = nominateByTier(workflow, remaining, resolveDisplayContext)
    if (!proposed) {
      break
    }
    if (wouldDrawPin(proposed)) {
      return proposed
    }
    remaining = remaining.filter(id => id !== proposed)
  }

  // Every tier walk stops before the project node, so a map that draws the workflow off the
  // project marker alone is never proposed. Ask the rest outright before calling it unpinned.
  return remaining.find(id => wouldDrawPin(id)) ?? null
}

const nominateByTier = (
  workflow: Workflow,
  candidateGlobeIds: string[],
  resolveDisplayContext: (
    locationId: string,
    candidateGlobeIds: string[],
  ) => { globeViewId: string | null } | null,
): string | null => {
  for (const l of workflow.locationsWithEquipment ?? []) {
    const globeViewId = resolveDisplayContext(
      l.id,
      candidateGlobeIds,
    )?.globeViewId
    if (globeViewId) {
      return globeViewId
    }
  }

  return null
}
